import type { AgentControlDefinition } from "./types";

import * as React from "react";

import { useAgentControlStore } from "./agentControlStore";

/**
 * Registers agent control definitions on mount and unregisters on unmount.
 * Definitions declare what functions exist in this context (even if their
 * implementations aren't mounted yet). This allows agents to discover
 * available functions via listFunctions().
 *
 * NOTE: `definitions` must be a stable reference (e.g. a module-level constant).
 * If an inline array literal is passed, the hook memoizes by serializing definition
 * names so it won't cause register/unregister loops on every render.
 */
export function useRegisterAgentControlDefinitions(
  definitions: AgentControlDefinition[],
) {
  const { registerDefinitions, unregisterDefinitions } = useAgentControlStore(
    (state) => state.actions,
  );

  // Memoize by definition names to guard against unstable array references
  const stableKey = definitions.map((item) => item.name).join(",");
  const stableDefinitions = React.useMemo(() => definitions, [stableKey]);

  // eslint-disable-next-line replo/no-use-effect -- registration side effect: register on mount, unregister on unmount
  React.useEffect(() => {
    registerDefinitions(stableDefinitions);
    return () => {
      unregisterDefinitions(stableDefinitions.map((item) => item.name));
    };
  }, [stableDefinitions, registerDefinitions, unregisterDefinitions]);
}

/**
 * Registers an agent control function implementation on mount and unregisters
 * on unmount. The function becomes callable via
 * window._reploAgentControl.call(name, ...args).
 *
 * The corresponding definition should be registered separately via
 * useRegisterAgentControlDefinitions so agents know about it even if
 * this component isn't mounted.
 */
export function useRegisterAgentControlFunction(
  name: string,
  fn: (...args: unknown[]) => unknown,
) {
  const { registerFunction, unregisterFunction } = useAgentControlStore(
    (state) => state.actions,
  );

  // eslint-disable-next-line replo/no-use-effect -- registration side effect: register on mount, unregister on unmount
  React.useEffect(() => {
    registerFunction(name, fn);
    return () => {
      unregisterFunction(name);
    };
  }, [name, fn, registerFunction, unregisterFunction]);
}
